import {
    ChevronLeftIcon,
    ChevronRightIcon,
    CrossIcon
} from "@solomon-labs/design/components/icons";
import { Text } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { Button } from "./button";
import { InputGroup, InputGroupInput } from "./input-group";

interface FieldRowsProps {
    /** The rows — one string value per row. */
    rows: string[];
    onChange: (next: string[]) => void;
    /** Placeholder for each field. */
    placeholder?: string;
    /** Sanitizes/filters raw input before it reaches `onChange`. */
    sanitize?: (raw: string) => string;
    /** Optional unit/decoration node rendered inside each field. */
    adornment?: React.ReactNode;
    /** "+Add" button label. */
    addLabel: string;
    /** aria-label for a row's remove button. */
    removeLabel?: string;
    /** Locked → fields greyed out and the remove X dropped, but still
     *  paginable. */
    disabled?: boolean;
    /** Rows per page (default 25). */
    pageSize?: number;
}

/**
 * FieldRows — a vertically-stacked list of single-field rows (each = one
 * boxed field + remove X), an "+Add" button, and 25/page pagination. The
 * Add button sits on the left and a `‹ n/N ›` pager on the right, sharing
 * one `justify-between` row. Generic over placeholder, sanitizer, adornment,
 * labels, disabled, and page size — it owns no toggle or gating; those stay
 * in the consumer.
 */
export function FieldRows({
    rows,
    onChange,
    placeholder,
    sanitize,
    adornment,
    addLabel,
    removeLabel = "Remove row",
    disabled = false,
    pageSize = 25
}: FieldRowsProps): React.ReactElement {
    const [page, setPage] = useState(0);

    const add = (): void => {
        onChange([...rows, ""]);
        setPage(Math.floor(rows.length / pageSize));
    };
    const update = (i: number, val: string): void =>
        onChange(rows.map((w, idx) => (idx === i ? val : w)));
    const remove = (i: number): void =>
        onChange(rows.filter((_, idx) => idx !== i));

    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    const pageRows = rows.slice(page * pageSize, (page + 1) * pageSize);

    return (
        <>
            {pageRows.map((w, i) => {
                const gi = page * pageSize + i;
                return (
                    <div
                        key={gi}
                        className={`grid items-center gap-2 ${
                            disabled
                                ? "grid-cols-[1fr]"
                                : "grid-cols-[1fr_2rem]"
                        }`}
                    >
                        <InputGroup className="h-11">
                            <InputGroupInput
                                value={w}
                                placeholder={placeholder}
                                disabled={disabled}
                                onChange={(e) =>
                                    update(
                                        gi,
                                        sanitize
                                            ? sanitize(e.target.value)
                                            : e.target.value
                                    )
                                }
                                numeric
                                className="text-left"
                            />
                            {adornment}
                        </InputGroup>
                        {disabled ? null : (
                            <button
                                type="button"
                                onClick={() => remove(gi)}
                                aria-label={removeLabel}
                                className="flex size-8 items-center justify-center text-error-base transition-opacity hover:opacity-70"
                            >
                                <CrossIcon className="size-6" />
                            </button>
                        )}
                    </div>
                );
            })}

            <div className="flex items-center justify-between">
                <Button
                    variant="sunken"
                    outline
                    interaction="lifted"
                    size="sm"
                    onClick={add}
                    disabled={disabled}
                >
                    {addLabel}
                </Button>
                {totalPages > 1 ? (
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            aria-label="Previous page"
                            className="text-foreground-muted transition-colors hover:text-foreground-base disabled:opacity-30"
                        >
                            <ChevronLeftIcon className="size-4" />
                        </button>
                        <Text variant="small" className="text-foreground-muted">
                            {page + 1} / {totalPages}
                        </Text>
                        <button
                            type="button"
                            onClick={() =>
                                setPage((p) => Math.min(totalPages - 1, p + 1))
                            }
                            disabled={page === totalPages - 1}
                            aria-label="Next page"
                            className="text-foreground-muted transition-colors hover:text-foreground-base disabled:opacity-30"
                        >
                            <ChevronRightIcon className="size-4" />
                        </button>
                    </div>
                ) : null}
            </div>
        </>
    );
}
